import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';
import {
  Building, Search, FileText, Users, Settings, LogOut,
  Plus, CalendarIcon, MapPin, DollarSign, Send, Eye, Trash2, AlertCircle, Mail, Bell, Clock, CheckCircle, ClipboardList, ExternalLink
} from 'lucide-react';

const ProductionDashboard = () => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const navItems = [
    { path: '/production', label: 'Find Stand-Ins', icon: Search },
    { path: '/production/requests', label: 'My Requests', icon: FileText },
    { path: '/production/call-sheets', label: 'Call Sheets', icon: ClipboardList },
    { path: '/production/bookings', label: 'Bookings', icon: Users },
    { path: '/production/invoices', label: 'Invoices', icon: DollarSign },
    { path: '/production/profile', label: 'My Profile', icon: Building },
    { path: '/production/settings', label: 'Settings', icon: Settings },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount(res.data.count);
    } catch (e) { /* silent */ }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(res.data);
    } catch (e) { console.error(e); }
  };

  const toggleNotifications = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#121212] border-r border-white/5 fixed h-screen overflow-y-auto">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-24 w-auto"
            />
          </Link>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <Building className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.full_name}</p>
              <p className="text-white/40 text-sm">Crowd 2nd</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Notification Bell */}
        <div className="flex justify-end mb-4 relative">
          <button onClick={toggleNotifications} className="relative p-2 rounded-lg bg-[#121212] border border-white/10 hover:border-[#D4AF37]/30 transition-colors" data-testid="production-notification-bell">
            <Bell className="w-5 h-5 text-white/60" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          {showNotifications && (
            <div className="absolute top-12 right-0 w-96 bg-[#121212] border border-white/10 rounded-xl shadow-2xl z-50 max-h-[70vh] overflow-hidden" data-testid="production-notification-dropdown">
              <div className="flex items-center justify-between p-4 border-b border-white/5">
                <h3 className="text-white font-semibold">Notifications</h3>
                {unreadCount > 0 && <button onClick={markAllRead} className="text-[#D4AF37] text-xs hover:underline">Mark all read</button>}
              </div>
              <div className="overflow-y-auto max-h-[60vh]">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center"><Bell className="w-8 h-8 text-white/20 mx-auto mb-2" /><p className="text-white/40 text-sm">No notifications yet</p></div>
                ) : notifications.map(n => (
                  <div key={n.id} className={`p-4 border-b border-white/5 ${!n.read ? 'bg-[#D4AF37]/5' : ''}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${n.notification_type === 'info' ? 'bg-green-500' : 'bg-[#D4AF37]'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium text-sm">{n.title}</p>
                        <p className="text-white/60 text-xs mt-0.5">{n.message}</p>
                        <p className="text-white/30 text-[10px] mt-1">{new Date(n.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {user?.status === 'pending' && (
          <div className="mb-6 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#F59E0B] font-medium">Account Pending Approval</p>
              <p className="text-[#F59E0B]/70 text-sm">You'll be able to submit requests once approved by 2nd Team admin.</p>
            </div>
          </div>
        )}
        
        <Routes>
          <Route index element={<RequestForm token={token} />} />
          <Route path="requests" element={<RequestsList token={token} />} />
          <Route path="call-sheets" element={<CallSheetManager token={token} />} />
          <Route path="bookings" element={<BookingsView token={token} />} />
          <Route path="invoices" element={<InvoicesView token={token} />} />
          <Route path="profile" element={<ProfileManager token={token} />} />
          <Route path="settings" element={<SettingsView />} />
        </Routes>
      </main>
    </div>
  );
};

// Call Sheet Manager - For Crowd 2nds to create and manage daily call sheets
const CallSheetManager = ({ token }) => {
  const { user } = useAuth();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [standins, setStandins] = useState([]);
  const [creating, setCreating] = useState(false);
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [generating, setGenerating] = useState(null);

  const [form, setForm] = useState({
    production_name: '',
    date: '',
    base_location: '',
    set_location: '',
    parking_location: '',
    colour_cover: '',
    notes: '',
  });
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    fetchSheets();
    fetchStandins();
  }, []);

  const fetchSheets = async () => {
    try {
      const res = await axios.get(`${API}/call-sheets`, { headers: { Authorization: `Bearer ${token}` } });
      setSheets(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStandins = async () => {
    try {
      // Get all standins via admin or bookings
      const res = await axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } });
      // Extract unique standin info from bookings
      const seen = new Set();
      const list = [];
      for (const b of res.data) {
        if (!seen.has(b.standin_id)) {
          seen.add(b.standin_id);
          list.push({ standin_id: b.standin_id, standin_name: b.standin_name, user_id: b.standin_id });
        }
      }
      setStandins(list);
    } catch (e) { console.error(e); }
  };

  const autoFillLocations = async (prodName) => {
    if (!prodName) return;
    try {
      const res = await axios.get(`${API}/call-sheets/autofill/${encodeURIComponent(prodName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm(prev => ({
        ...prev,
        base_location: res.data.base_location || prev.base_location,
        set_location: res.data.set_location || prev.set_location,
        parking_location: res.data.parking_location || prev.parking_location,
      }));
    } catch (e) { /* silent */ }
  };

  const addEntry = () => setEntries([...entries, { standin_user_id: '', call_time: '' }]);
  const removeEntry = (idx) => setEntries(entries.filter((_, i) => i !== idx));
  const updateEntry = (idx, field, value) => {
    const updated = [...entries];
    updated[idx] = { ...updated[idx], [field]: value };
    setEntries(updated);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.production_name || !form.date) { toast.error('Production name and date required'); return; }
    if (entries.length === 0 || entries.some(e => !e.standin_user_id)) { toast.error('Add at least one stand-in'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/call-sheets`, {
        ...form,
        entries: entries.map(e => ({ standin_user_id: e.standin_user_id, call_time: e.call_time || null }))
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Call sheet published — stand-ins notified');
      setShowCreate(false);
      setForm({ production_name: '', date: '', base_location: '', set_location: '', parking_location: '', colour_cover: '', notes: '' });
      setEntries([]);
      fetchSheets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create call sheet'); }
    finally { setCreating(false); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'arrived': return 'bg-green-500/20 text-green-400';
      case 'wrapped': return 'bg-blue-500/20 text-blue-400';
      default: return 'bg-white/10 text-white/40';
    }
  };

  const getArrivalStats = (sheet) => {
    const total = sheet.entries?.length || 0;
    const arrived = sheet.entries?.filter(e => e.status === 'arrived' || e.status === 'wrapped').length || 0;
    const wrapped = sheet.entries?.filter(e => e.status === 'wrapped').length || 0;
    return { total, arrived, wrapped };
  };

  const handleGenerateInvoice = async (sheetId) => {
    setGenerating(sheetId);
    try {
      const res = await axios.post(`${API}/call-sheets/${sheetId}/generate-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice ${res.data.invoice_number} generated — £${parseFloat(res.data.total).toFixed(2)} total`);
      fetchSheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white">Call Sheets</h1>
          <p className="text-white/60 mt-1">Create daily call sheets and track stand-in arrivals</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="gold-gradient text-black font-semibold" data-testid="create-call-sheet-btn">
          <Plus className="w-4 h-4 mr-2" /> New Call Sheet
        </Button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6" data-testid="call-sheet-form">
          <h2 className="text-white font-semibold mb-4">Create Call Sheet</h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Production Name *</Label>
              <Input
                value={form.production_name}
                onChange={e => { setForm({ ...form, production_name: e.target.value }); }}
                onBlur={() => autoFillLocations(form.production_name)}
                placeholder="e.g. The Crown S7"
                className="bg-[#0A0A0A] border-white/10 text-white"
                data-testid="cs-production-name"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Shooting Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="cs-date" />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Base Location</Label>
              <Input value={form.base_location} onChange={e => setForm({ ...form, base_location: e.target.value })} placeholder="e.g. Pinewood Studios, Unit Base" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="cs-base-location" />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Set Location</Label>
              <Input value={form.set_location} onChange={e => setForm({ ...form, set_location: e.target.value })} placeholder="e.g. Stage 4, Pinewood" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="cs-set-location" />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Parking Location</Label>
              <Input value={form.parking_location} onChange={e => setForm({ ...form, parking_location: e.target.value })} placeholder="e.g. Car Park B" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="cs-parking" />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Colour Cover</Label>
              <Input value={form.colour_cover} onChange={e => setForm({ ...form, colour_cover: e.target.value })} placeholder="e.g. Dark suit, blue shirt" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="cs-colour-cover" />
            </div>
          </div>

          <div className="mb-4">
            <Label className="text-white/80 mb-1 block text-sm">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white resize-none" rows={2} placeholder="Any special instructions for the day..." />
          </div>

          {/* Stand-in Entries */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-white/80 text-sm font-semibold">Stand-ins on Call</Label>
              <Button type="button" onClick={addEntry} variant="ghost" size="sm" className="text-[#D4AF37] hover:text-[#D4AF37]/80 text-xs" data-testid="add-standin-entry">
                <Plus className="w-3 h-3 mr-1" /> Add Stand-in
              </Button>
            </div>
            {entries.length === 0 && (
              <p className="text-white/30 text-sm p-4 bg-[#0A0A0A] rounded-lg border border-dashed border-white/10 text-center">
                No stand-ins added yet. Click "Add Stand-in" above.
              </p>
            )}
            <div className="space-y-2">
              {entries.map((entry, idx) => (
                <div key={idx} className="flex items-end gap-3 bg-[#0A0A0A] rounded-lg p-3 border border-white/5" data-testid={`cs-entry-${idx}`}>
                  <div className="flex-1">
                    <Label className="text-white/50 text-xs block mb-1">Stand-in</Label>
                    <Input
                      value={entry.standin_user_id}
                      onChange={e => updateEntry(idx, 'standin_user_id', e.target.value)}
                      placeholder="Stand-in user ID"
                      className="bg-[#121212] border-white/10 text-white text-xs h-9"
                      list={`standins-list-${idx}`}
                    />
                    <datalist id={`standins-list-${idx}`}>
                      {standins.map(s => (
                        <option key={s.standin_id} value={s.user_id || s.standin_id}>{s.standin_name}</option>
                      ))}
                    </datalist>
                  </div>
                  <div className="w-32">
                    <Label className="text-white/50 text-xs block mb-1">Call Time</Label>
                    <Input type="time" value={entry.call_time} onChange={e => updateEntry(idx, 'call_time', e.target.value)} className="bg-[#121212] border-white/10 text-white text-xs h-9" data-testid={`cs-call-time-${idx}`} />
                  </div>
                  <button type="button" onClick={() => removeEntry(idx)} className="text-red-400/60 hover:text-red-400 h-9 flex items-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={creating} className="gold-gradient text-black font-semibold" data-testid="publish-call-sheet-btn">
              {creating ? 'Publishing...' : 'Publish Call Sheet'}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setShowCreate(false); setEntries([]); }} className="border-white/20 text-white/60">Cancel</Button>
          </div>
        </form>
      )}

      {/* Call Sheet List */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div></div>
      ) : sheets.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <ClipboardList className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No call sheets yet. Create one for tomorrow's shoot.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sheets.map(sheet => {
            const stats = getArrivalStats(sheet);
            const isExpanded = expandedSheet === sheet.id;

            return (
              <div key={sheet.id} className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden" data-testid={`call-sheet-${sheet.id}`}>
                <div
                  className="p-5 cursor-pointer hover:bg-white/[0.02] transition-colors"
                  onClick={() => setExpandedSheet(isExpanded ? null : sheet.id)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-white font-semibold">{sheet.production_name}</h3>
                        <span className="text-[#D4AF37] text-sm">{sheet.date}</span>
                      </div>
                      <div className="flex items-center gap-4 text-white/40 text-sm">
                        {sheet.base_location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{sheet.base_location}</span>}
                        {sheet.colour_cover && <span>Cover: {sheet.colour_cover}</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2">
                        <span className="text-green-400 text-sm font-medium">{stats.arrived}/{stats.total} arrived</span>
                        {stats.wrapped > 0 && <span className="text-blue-400 text-sm">{stats.wrapped} wrapped</span>}
                      </div>
                      <div className="w-32 h-1.5 bg-white/10 rounded-full mt-2">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${stats.total > 0 ? (stats.arrived / stats.total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded: show entries */}
                {isExpanded && (
                  <div className="border-t border-white/5 p-5">
                    <table className="w-full">
                      <thead>
                        <tr className="text-white/40 text-xs uppercase tracking-wider">
                          <th className="text-left pb-3">Stand-in</th>
                          <th className="text-left pb-3">Call Time</th>
                          <th className="text-left pb-3">Arrived</th>
                          <th className="text-left pb-3">Wrapped</th>
                          <th className="text-left pb-3">Status</th>
                          <th className="text-right pb-3">Day Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sheet.entries?.map((entry, idx) => (
                          <tr key={idx} className="border-t border-white/5">
                            <td className="py-3 text-white font-medium text-sm">{entry.standin_name}</td>
                            <td className="py-3 text-white/60 text-sm">{entry.call_time || 'TBC'}</td>
                            <td className="py-3 text-white/60 text-sm">{entry.arrived_at || '—'}</td>
                            <td className="py-3 text-white/60 text-sm">{entry.wrapped_at || '—'}</td>
                            <td className="py-3">
                              <span className={`px-2 py-0.5 text-xs rounded capitalize ${getStatusColor(entry.status)}`}>
                                {entry.status}
                              </span>
                            </td>
                            <td className="py-3 text-white text-sm text-right">£{entry.day_rate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sheet.set_location && <p className="text-white/30 text-xs mt-3">Set: {sheet.set_location}</p>}
                    {sheet.parking_location && <p className="text-white/30 text-xs">Parking: {sheet.parking_location}</p>}
                    {sheet.notes && <p className="text-white/30 text-xs mt-1">Notes: {sheet.notes}</p>}

                    {/* Generate Invoice Button */}
                    <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                      {sheet.invoice_number ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 text-sm font-medium">Invoice {sheet.invoice_number} generated</span>
                        </div>
                      ) : stats.wrapped > 0 ? (
                        <Button
                          onClick={() => handleGenerateInvoice(sheet.id)}
                          disabled={generating === sheet.id}
                          className="gold-gradient text-black font-semibold"
                          data-testid={`generate-invoice-${sheet.id}`}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          {generating === sheet.id ? 'Generating...' : `Generate Invoice (${stats.wrapped} stand-in${stats.wrapped !== 1 ? 's' : ''})`}
                        </Button>
                      ) : (
                        <p className="text-white/30 text-sm">Invoice can be generated once stand-ins are wrapped</p>
                      )}
                      <span className="text-white/20 text-xs">{stats.wrapped}/{stats.total} wrapped</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Profile Manager - For Crowd 2nds to manage their productions
const ProfileManager = ({ token }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    job_title: 'Crowd 2nd',
    phone: '',
    billing_address: ''
  });

  const [productions, setProductions] = useState([]);
  const [newProduction, setNewProduction] = useState({
    production_name: '',
    work_email: '',
    role_title: 'Crowd 2nd',
    is_current: false
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/production/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        job_title: response.data.job_title || 'Crowd 2nd',
        phone: response.data.phone || '',
        billing_address: response.data.billing_address || ''
      });
      setProductions(response.data.productions || []);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/production/profile`, {
        full_name: formData.full_name,
        job_title: formData.job_title,
        phone: formData.phone,
        billing_address: formData.billing_address,
        productions: productions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile saved successfully');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addProduction = async () => {
    if (!newProduction.production_name || !newProduction.work_email) {
      toast.error('Please fill in production name and work email');
      return;
    }

    try {
      await axios.post(`${API}/production/add-production-email`, newProduction, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductions([...productions, newProduction]);
      setNewProduction({
        production_name: '',
        work_email: '',
        role_title: 'Crowd 2nd',
        is_current: false
      });
      toast.success('Production added');
    } catch (error) {
      toast.error('Failed to add production');
    }
  };

  const removeProduction = async (productionName) => {
    try {
      await axios.delete(`${API}/production/remove-production/${encodeURIComponent(productionName)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductions(productions.filter(p => p.production_name !== productionName));
      toast.success('Production removed');
    } catch (error) {
      toast.error('Failed to remove production');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white">My Profile</h1>
          <p className="text-white/60 mt-1">Manage your details and production work emails</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gold-gradient text-black font-semibold hover:brightness-110"
          data-testid="save-profile-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Basic Info */}
      <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
        <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Personal Details</h2>
        <p className="text-white/40 text-sm mb-6">Your personal email ({user?.email}) is used for login</p>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-white/80 mb-2 block">Full Name *</Label>
            <Input
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Your full name"
              className="bg-[#0A0A0A] border-white/10 text-white h-12"
            />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block">Job Title</Label>
            <Select value={formData.job_title} onValueChange={(v) => setFormData({ ...formData, job_title: v })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Crowd 2nd">Crowd 2nd</SelectItem>
                <SelectItem value="1st AD">1st AD</SelectItem>
                <SelectItem value="2nd AD">2nd AD</SelectItem>
                <SelectItem value="Production Coordinator">Production Coordinator</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/80 mb-2 block">Phone Number</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+44 7700 000000"
              className="bg-[#0A0A0A] border-white/10 text-white h-12"
            />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block">Billing Address</Label>
            <Input
              value={formData.billing_address}
              onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
              placeholder="Address for invoices"
              className="bg-[#0A0A0A] border-white/10 text-white h-12"
            />
          </div>
        </div>
      </div>

      {/* Productions & Work Emails */}
      <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white">Productions & Work Emails</h2>
            <p className="text-white/40 text-sm mt-1">Add each production you work on with your work email for that production</p>
          </div>
        </div>

        {/* Existing Productions */}
        {productions.length > 0 && (
          <div className="space-y-3 mb-6">
            {productions.map((prod, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-white/5">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium">{prod.production_name}</p>
                    {prod.is_current && (
                      <span className="px-2 py-0.5 bg-[#10B981]/20 text-[#10B981] text-xs rounded">Current</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-white/60 text-sm">
                    <Mail className="w-3 h-3" />
                    <span>{prod.work_email}</span>
                    {prod.role_title && <span className="text-white/40">• {prod.role_title}</span>}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeProduction(prod.production_name)}
                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add New Production */}
        <div className="p-4 bg-[#0A0A0A] rounded-lg border border-dashed border-white/10">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#D4AF37]" />
            Add New Production
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/60 text-sm mb-1 block">Production Name</Label>
              <Input
                value={newProduction.production_name}
                onChange={(e) => setNewProduction({ ...newProduction, production_name: e.target.value })}
                placeholder="e.g., The Crown Season 7"
                className="bg-[#121212] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/60 text-sm mb-1 block">Work Email for this Production</Label>
              <Input
                type="email"
                value={newProduction.work_email}
                onChange={(e) => setNewProduction({ ...newProduction, work_email: e.target.value })}
                placeholder="your.name@production.com"
                className="bg-[#121212] border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-white/60 text-sm mb-1 block">Your Role</Label>
              <Select 
                value={newProduction.role_title} 
                onValueChange={(v) => setNewProduction({ ...newProduction, role_title: v })}
              >
                <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Crowd 2nd">Crowd 2nd</SelectItem>
                  <SelectItem value="1st AD">1st AD</SelectItem>
                  <SelectItem value="2nd AD">2nd AD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={newProduction.is_current}
                  onCheckedChange={(c) => setNewProduction({ ...newProduction, is_current: c })}
                  className="data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                />
                <span className="text-white/60 text-sm">Currently working on this</span>
              </label>
            </div>
          </div>
          <Button
            onClick={addProduction}
            className="mt-4 gold-gradient text-black font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Production
          </Button>
        </div>
      </div>
    </div>
  );
};

// Request Form Component
const RequestForm = ({ token }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [formData, setFormData] = useState({
    production_name: '',
    performer_type: 'standin',
    actor_name: '',
    gender: '',
    height_min_cm: '',
    eye_color: '',
    hair_color: '',
    skin_tone: '',
    cast_age: 'N/A',
    physical_fitness: '',
    desired_measurements: '',
    date_notes: '',
    location: '',
    rate_offered: 250,
    expected_hours: 11,
    additional_requirements: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (user?.status !== 'approved') {
      toast.error('Your account must be approved to submit requests');
      return;
    }

    if (!formData.gender) {
      toast.error('Gender is required');
      return;
    }

    if (!formData.rate_offered) {
      toast.error('Day rate is required');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/production/request`, {
        production_name: formData.production_name,
        performer_type: formData.performer_type || 'standin',
        actor_name: formData.actor_name || null,
        gender: formData.gender || null,
        height_min_cm: formData.height_min_cm ? parseFloat(formData.height_min_cm) : null,
        height_max_cm: formData.height_min_cm ? parseFloat(formData.height_min_cm) : null,
        eye_color: formData.eye_color || null,
        hair_color: formData.hair_color || null,
        skin_tone: formData.skin_tone || null,
        cast_age: formData.cast_age || null,
        physical_fitness: formData.physical_fitness || null,
        desired_measurements: formData.desired_measurements || null,
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
        dates_needed: [],
        date_notes: formData.date_notes || null,
        location: formData.location,
        rate_offered: parseFloat(formData.rate_offered),
        expected_hours: formData.expected_hours ? parseFloat(formData.expected_hours) : null,
        additional_requirements: formData.additional_requirements || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Request submitted successfully');
      setFormData({
        production_name: '', performer_type: 'standin', actor_name: '', gender: '',
        height_min_cm: '', eye_color: '', hair_color: '', skin_tone: '',
        cast_age: 'N/A', physical_fitness: '', desired_measurements: '', date_notes: '',
        location: '', rate_offered: 250, additional_requirements: ''
      });
      setStartDate(null);
      setEndDate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Request a Performer</h1>
      <p className="text-white/60 mb-8">Fill in the characteristics of the actor you need a stand-in or double for</p>

      <form onSubmit={handleSubmit}>
        {/* Production Details */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Production Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80 mb-2 block">Production Name *</Label>
              <Input
                value={formData.production_name}
                onChange={(e) => setFormData({ ...formData, production_name: e.target.value })}
                placeholder="e.g., The Crown Season 7"
                required
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="production-name-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">What type of performer?</Label>
              <Select value={formData.performer_type} onValueChange={(v) => setFormData({ ...formData, performer_type: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="performer-type-select">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standin">Stand-in</SelectItem>
                  <SelectItem value="double">Double</SelectItem>
                  <SelectItem value="special_feature">Special Feature</SelectItem>
                  <SelectItem value="spact">SPACT</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-white/80 mb-2 block">Main Cast Actor Name (if known)</Label>
              <Input
                value={formData.actor_name}
                onChange={(e) => setFormData({ ...formData, actor_name: e.target.value })}
                placeholder="e.g., Emma Stone"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="actor-name-input"
              />
            </div>
          </div>
        </div>

        {/* Character Requirements */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Cast Characteristics</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-white/80 mb-2 block">Main Cast Gender</Label>
              <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="gender-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non_binary">Neutral</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Cast Hair Colour</Label>
              <Select value={formData.hair_color} onValueChange={(v) => setFormData({ ...formData, hair_color: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="hair-color-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="black">Black</SelectItem>
                  <SelectItem value="brown">Brown</SelectItem>
                  <SelectItem value="blonde">Blonde</SelectItem>
                  <SelectItem value="red">Red / Auburn</SelectItem>
                  <SelectItem value="grey">Grey</SelectItem>
                  <SelectItem value="white">White</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Cast Skin Tone</Label>
              <Select value={formData.skin_tone} onValueChange={(v) => setFormData({ ...formData, skin_tone: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="skin-tone-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="very_fair">Very Fair</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="olive">Olive</SelectItem>
                  <SelectItem value="tan">Tan</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="very_dark">Very Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Cast Height (cm)</Label>
              <Input
                type="number"
                value={formData.height_min_cm}
                onChange={(e) => setFormData({ ...formData, height_min_cm: e.target.value })}
                placeholder="175"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="height-min-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Cast Eye Colour</Label>
              <Select value={formData.eye_color} onValueChange={(v) => setFormData({ ...formData, eye_color: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="eye-color-select">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="brown">Brown</SelectItem>
                  <SelectItem value="blue">Blue</SelectItem>
                  <SelectItem value="green">Green</SelectItem>
                  <SelectItem value="hazel">Hazel</SelectItem>
                  <SelectItem value="grey">Grey</SelectItem>
                  <SelectItem value="amber">Amber</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Cast Age</Label>
              <Input
                value={formData.cast_age}
                onChange={(e) => setFormData({ ...formData, cast_age: e.target.value })}
                placeholder="N/A"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="cast-age-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label className="text-white/80 mb-2 block">Physical Fitness Requirements</Label>
              <Textarea
                value={formData.physical_fitness}
                onChange={(e) => setFormData({ ...formData, physical_fitness: e.target.value })}
                placeholder="Any specific physical fitness requirements..."
                rows={2}
                className="bg-[#0A0A0A] border-white/10 text-white resize-none"
                data-testid="fitness-textarea"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Desired Body Measurements</Label>
              <Textarea
                value={formData.desired_measurements}
                onChange={(e) => setFormData({ ...formData, desired_measurements: e.target.value })}
                placeholder="e.g., chest 38, waist 32, hips 34..."
                rows={2}
                className="bg-[#0A0A0A] border-white/10 text-white resize-none"
                data-testid="measurements-textarea"
              />
            </div>
          </div>
        </div>

        {/* Dates & Location */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Dates & Location</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-white/80 mb-2 block">Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-[#0A0A0A] border-white/10 text-white hover:bg-[#121212] h-12"
                    data-testid="start-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    {startDate ? format(startDate, 'dd MMM yyyy') : 'Select start date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Finish Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal bg-[#0A0A0A] border-white/10 text-white hover:bg-[#121212] h-12"
                    data-testid="end-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                    {endDate ? format(endDate, 'dd MMM yyyy') : 'Select finish date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    className="rounded-md"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div>
              <Label className="text-white/80 mb-2 block">Location *</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Pinewood Studios"
                required
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="location-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Notes on Dates</Label>
              <Input
                value={formData.date_notes}
                onChange={(e) => setFormData({ ...formData, date_notes: e.target.value })}
                placeholder="e.g., Screen tests, Fittings etc"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="date-notes-input"
              />
            </div>
          </div>
        </div>

        {/* Rate */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Rate</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/80 mb-2 block">Day Rate Offered (£) *</Label>
              <Input
                type="number"
                value={formData.rate_offered}
                onChange={(e) => setFormData({ ...formData, rate_offered: e.target.value })}
                placeholder="250"
                required
                min="1"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="rate-input"
              />
              <p className="text-white/40 text-sm mt-1">Standard UK rate is £250/day</p>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Expected Hours *</Label>
              <Input
                type="number"
                value={formData.expected_hours}
                onChange={(e) => setFormData({ ...formData, expected_hours: e.target.value })}
                placeholder="11"
                required
                min="1"
                max="24"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="expected-hours-input"
              />
              <p className="text-white/40 text-sm mt-1">Typical day is 11 hours</p>
            </div>
          </div>
        </div>

        {/* Additional Requirements */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Additional Requirements</h2>
          <Textarea
            value={formData.additional_requirements}
            onChange={(e) => setFormData({ ...formData, additional_requirements: e.target.value })}
            placeholder="Any other specific requirements (e.g., driving licence, specific skills, costume requirements)..."
            rows={4}
            className="bg-[#0A0A0A] border-white/10 text-white resize-none"
            data-testid="requirements-textarea"
          />
        </div>

        <Button
          type="submit"
          disabled={loading || user?.status !== 'approved'}
          className="gold-gradient text-black font-bold px-8 py-3 uppercase tracking-wider hover:brightness-110 disabled:opacity-50"
          data-testid="submit-request-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </form>
    </div>
  );
};

// Requests List Component
const RequestsList = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [responses, setResponses] = useState(null);
  const [loadingResponses, setLoadingResponses] = useState(false);
  const [booking, setBooking] = useState(null);
  const [viewProfile, setViewProfile] = useState(null);

  useEffect(() => {
    fetchRequests();
    const interval = setInterval(fetchRequests, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/production/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResponses = async (requestId) => {
    if (expandedRequest === requestId) {
      setExpandedRequest(null);
      return;
    }
    setExpandedRequest(requestId);
    setLoadingResponses(true);
    try {
      const res = await axios.get(`${API}/production/request/${requestId}/responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponses(res.data);
    } catch (e) {
      toast.error('Failed to load responses');
    } finally { setLoadingResponses(false); }
  };

  const handleBook = async (requestId, standinId, name) => {
    setBooking(standinId);
    try {
      const res = await axios.post(`${API}/production/request/${requestId}/book/${standinId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchRequests();
      fetchResponses(requestId);
      setExpandedRequest(requestId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to book');
    } finally { setBooking(null); }
  };

  const formatHeight = (m) => {
    if (!m) return '';
    if (m.height_ft && m.height_in !== undefined) return `${m.height_ft}'${Math.round(m.height_in)}"`;
    if (m.height_cm) return `${Math.round(m.height_cm)}cm`;
    return '';
  };

  const proxyImg = (url) => {
    if (!url) return '';
    if (url.startsWith('/') || url.includes(window.location.host)) return url;
    return `${API}/image-proxy?url=${encodeURIComponent(url)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white">My Requests</h1>
          <p className="text-white/60 mt-1">Track availability responses and book stand-ins</p>
        </div>
        <Link to="/production">
          <Button className="gold-gradient text-black font-semibold">
            <Plus className="w-4 h-4 mr-2" />
            New Request
          </Button>
        </Link>
      </div>

      {requests.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No requests yet</p>
          <Link to="/production">
            <Button className="mt-4 gold-gradient text-black font-semibold">
              Create Your First Request
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div key={request.id} className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-colors" data-testid={`request-${request.id}`}>
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-white font-semibold text-lg">{request.production_name}</h3>
                    {request.actor_name && (
                      <p className="text-[#D4AF37] text-sm">Stand-in for: {request.actor_name}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-white/60 text-sm">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="w-4 h-4" />
                        {request.dates_needed?.length || 0} day(s)
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {request.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        £{request.rate_offered}/day
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {request.gender && <span className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded capitalize">{request.gender}</span>}
                      {request.hair_color && <span className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded capitalize">{request.hair_color} hair</span>}
                      {request.skin_tone && <span className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded capitalize">{request.skin_tone.replace('_', ' ')}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                      request.status === 'booked' ? 'bg-[#10B981]/20 text-[#10B981]' :
                      request.status === 'checking_availability' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                      request.status === 'pending' ? 'bg-white/10 text-white/40' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {request.status === 'checking_availability' ? 'Awaiting Responses' : request.status}
                    </span>
                    {request.matched_standins?.length > 0 && (
                      <p className="text-white/40 text-sm mt-2">{request.matched_standins.length} sent</p>
                    )}
                  </div>
                </div>

                {/* View Responses Button */}
                {(request.status === 'checking_availability' || request.status === 'booked') && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchResponses(request.id)}
                    className="mt-4 border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    data-testid={`view-responses-${request.id}`}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {expandedRequest === request.id ? 'Hide Responses' : 'View Responses'}
                  </Button>
                )}
              </div>

              {/* Expanded Responses */}
              {expandedRequest === request.id && (
                <div className="border-t border-white/5 bg-[#0A0A0A] p-6">
                  {loadingResponses ? (
                    <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div></div>
                  ) : responses ? (
                    <>
                      <div className="flex items-center gap-4 mb-4 text-sm">
                        <span className="text-[#10B981]">{responses.available_count} available</span>
                        <span className="text-[#F59E0B]">{responses.pending_count} pending</span>
                        <span className="text-white/30">{responses.total_sent} sent</span>
                      </div>

                      {responses.responses.length === 0 ? (
                        <p className="text-white/40 text-sm">No responses yet — stand-ins have been notified.</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {responses.responses.map((r) => (
                            <div
                              key={r.check_id}
                              className={`rounded-xl border p-4 ${
                                r.status === 'booked' ? 'border-[#10B981]/50 bg-[#10B981]/5' :
                                r.status === 'responded_available' ? 'border-[#D4AF37]/30 bg-[#121212]' :
                                r.status === 'rejected' ? 'border-white/5 bg-[#121212] opacity-50' :
                                r.status === 'responded_unavailable' ? 'border-red-500/20 bg-[#121212] opacity-40' :
                                'border-white/5 bg-[#121212]'
                              }`}
                              data-testid={`response-card-${r.standin_id}`}
                            >
                              <div className="flex items-start gap-4">
                                {/* Photo or Initial */}
                                {r.headshot_url || (r.photos && r.photos.length > 0) ? (
                                  <img
                                    src={proxyImg(r.headshot_url || r.photos[0])}
                                    alt={r.full_name}
                                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold text-xl flex-shrink-0">
                                    {r.first_name?.[0] || '?'}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h4 className="text-white font-semibold truncate">{r.status === 'booked' ? r.full_name : r.first_name}</h4>
                                    <span className={`px-2 py-0.5 text-xs rounded capitalize ${
                                      r.status === 'booked' ? 'bg-[#10B981]/20 text-[#10B981]' :
                                      r.status === 'responded_available' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                                      r.status === 'responded_unavailable' ? 'bg-red-500/20 text-red-400' :
                                      r.status === 'rejected' ? 'bg-white/10 text-white/40' :
                                      'bg-[#F59E0B]/20 text-[#F59E0B]'
                                    }`}>
                                      {r.status === 'responded_available' ? 'Available' :
                                       r.status === 'responded_unavailable' ? 'Unavailable' :
                                       r.status === 'booked' ? 'Booked' :
                                       r.status === 'rejected' ? 'Not Chosen' :
                                       'Pending'}
                                    </span>
                                  </div>
                                  {/* Key stats row */}
                                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-white/60 text-xs">
                                    {r.gender && <span className="capitalize">{r.gender}</span>}
                                    {formatHeight(r.measurements) && <span>{formatHeight(r.measurements)}</span>}
                                    {r.hair_color && <span className="capitalize">{r.hair_color} hair</span>}
                                    {r.skin_tone && <span className="capitalize">{r.skin_tone.replace('_', ' ')}</span>}
                                  </div>
                                  {/* Experience snippet */}
                                  {r.experiences?.length > 0 && (
                                    <p className="text-white/30 text-xs mt-1 truncate">
                                      {r.experiences.slice(0, 2).map(e => e.production || e).join(', ')}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex gap-2 mt-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setViewProfile(r)}
                                  className="border-white/10 text-white/60 text-xs flex-1"
                                  data-testid={`view-profile-${r.standin_id}`}
                                >
                                  <Eye className="w-3 h-3 mr-1" /> View Profile
                                </Button>
                                {r.status === 'responded_available' && !responses.booked_standin_id && (
                                  <Button
                                    size="sm"
                                    onClick={() => handleBook(request.id, r.standin_id, r.full_name)}
                                    disabled={booking === r.standin_id}
                                    className="gold-gradient text-black font-semibold text-xs flex-1"
                                    data-testid={`book-standin-${r.standin_id}`}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {booking === r.standin_id ? 'Booking...' : 'Book Me'}
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Profile CV / Comp Card Modal — NO contact details */}
      {viewProfile && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setViewProfile(null)}>
          <div className="bg-[#121212] border border-white/10 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="profile-modal">
            {/* Photos Hero Section */}
            <div className="p-6 pb-0">
              <div className="flex gap-3">
                {/* Headshot - large */}
                <div className="flex-1">
                  {viewProfile.headshot_url ? (
                    <img src={proxyImg(viewProfile.headshot_url)} alt="Headshot" className="w-full aspect-[3/4] object-cover rounded-xl" />
                  ) : (
                    <div className="w-full aspect-[3/4] rounded-xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] font-bold text-6xl">
                      {viewProfile.first_name?.[0] || '?'}
                    </div>
                  )}
                  <p className="text-white/30 text-xs mt-1 text-center">Headshot</p>
                </div>
                {/* Full Body - large */}
                <div className="flex-1">
                  {viewProfile.full_body_url ? (
                    <img src={proxyImg(viewProfile.full_body_url)} alt="Full body" className="w-full aspect-[3/4] object-cover rounded-xl" />
                  ) : (
                    <div className="w-full aspect-[3/4] rounded-xl bg-white/5 flex items-center justify-center text-white/20 text-sm">
                      No full body photo
                    </div>
                  )}
                  <p className="text-white/30 text-xs mt-1 text-center">Full Body</p>
                </div>
                {/* Additional photos if any */}
                {viewProfile.photos?.filter(p => p !== viewProfile.headshot_url && p !== viewProfile.full_body_url).length > 0 && (
                  <div className="flex-1">
                    <img src={proxyImg(viewProfile.photos.filter(p => p !== viewProfile.headshot_url && p !== viewProfile.full_body_url)[0])} alt="Additional" className="w-full aspect-[3/4] object-cover rounded-xl" />
                    <p className="text-white/30 text-xs mt-1 text-center">Additional</p>
                  </div>
                )}
              </div>
            </div>

            {/* Name + Key Info */}
            <div className="px-6 pt-4 pb-2">
              <h2 className="font-['Playfair_Display'] text-white font-bold text-2xl">{viewProfile.status === 'booked' ? viewProfile.full_name : viewProfile.first_name}</h2>
              <p className="text-[#D4AF37] text-sm capitalize mb-4">{viewProfile.experience_level?.replace(/_/g, ' ') || 'Stand-In'}</p>
            </div>

            {/* Stats Grid */}
            <div className="px-6 pb-4">
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Gender', value: viewProfile.gender || '-' },
                  { label: 'Height', value: viewProfile.measurements?.height_ft ? `${viewProfile.measurements.height_ft}'${Math.round(viewProfile.measurements.height_in || 0)}"` : '-' },
                  { label: 'Hair Colour', value: viewProfile.hair_color || '-' },
                  { label: 'Skin Tone', value: viewProfile.skin_tone?.replace('_', ' ') || '-' },
                ].map((stat, i) => (
                  <div key={i} className="bg-[#0A0A0A] rounded-lg p-3 text-center">
                    <p className="text-white/40 text-xs mb-1">{stat.label}</p>
                    <p className="text-white font-semibold text-sm capitalize">{stat.value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 pb-6 space-y-5">
              {/* Contact Details — Only visible after booking */}
              {viewProfile.status === 'booked' && (viewProfile.contact_email || viewProfile.phone || viewProfile.address) && (
                <div>
                  <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3 font-semibold">Contact Details</h3>
                  <div className="bg-[#0A0A0A] rounded-lg p-4 space-y-2">
                    {viewProfile.contact_email && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">Email</span>
                        <a href={`mailto:${viewProfile.contact_email}`} className="text-[#D4AF37] hover:underline">{viewProfile.contact_email}</a>
                      </div>
                    )}
                    {viewProfile.phone && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">Phone</span>
                        <a href={`tel:${viewProfile.phone}`} className="text-white">{viewProfile.phone}</a>
                      </div>
                    )}
                    {viewProfile.address && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">Address</span>
                        <span className="text-white text-right">{viewProfile.address}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Measurements */}
              <div>
                <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3 font-semibold">Measurements</h3>
                <div className="bg-[#0A0A0A] rounded-lg p-4">
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { label: 'Height', value: viewProfile.measurements?.height_cm ? `${viewProfile.measurements.height_ft}'${Math.round(viewProfile.measurements.height_in || 0)}" (${Math.round(viewProfile.measurements.height_cm)}cm)` : null },
                      { label: 'Chest/Bust', value: viewProfile.measurements?.chest_bust ? `${viewProfile.measurements.chest_bust}"` : null },
                      { label: 'Waist', value: viewProfile.measurements?.waist ? `${viewProfile.measurements.waist}"` : null },
                      { label: 'Hips', value: viewProfile.measurements?.hips ? `${viewProfile.measurements.hips}"` : null },
                      { label: 'Neck', value: viewProfile.measurements?.neck ? `${viewProfile.measurements.neck}"` : null },
                      { label: 'Inside Leg', value: viewProfile.measurements?.inside_leg ? `${viewProfile.measurements.inside_leg}"` : null },
                      { label: 'Shoe Size', value: viewProfile.measurements?.shoe_size || null },
                    ].filter(m => m.value).map((m, i) => (
                      <div key={i} className="text-center">
                        <p className="text-white/40 text-xs mb-1">{m.label}</p>
                        <p className="text-white font-medium text-sm">{m.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Previous Productions */}
              {viewProfile.experiences?.length > 0 && (
                <div>
                  <h3 className="text-[#D4AF37] text-xs uppercase tracking-wider mb-3 font-semibold">Previous Productions</h3>
                  <div className="bg-[#0A0A0A] rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-white/40 text-xs font-medium p-3">Production</th>
                          <th className="text-left text-white/40 text-xs font-medium p-3">Actor</th>
                          <th className="text-right text-white/40 text-xs font-medium p-3">Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewProfile.experiences.map((exp, i) => (
                          <tr key={i} className="border-b border-white/5 last:border-0">
                            <td className="p-3 text-white font-medium">{exp.production_name || exp.production || (typeof exp === 'string' ? exp : '-')}</td>
                            <td className="p-3 text-white/60">{exp.actor_name || '-'}</td>
                            <td className="p-3 text-white/60 text-right">{exp.days_worked || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Footer with Book button */}
            <div className="border-t border-white/5 px-6 py-4 flex justify-end gap-3 sticky bottom-0 bg-[#121212] rounded-b-xl">
              {viewProfile.status === 'responded_available' && !responses?.booked_standin_id && (
                <Button
                  onClick={() => { handleBook(expandedRequest, viewProfile.standin_id, viewProfile.first_name); setViewProfile(null); }}
                  className="gold-gradient text-black font-semibold"
                  data-testid="book-from-modal"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Book {viewProfile.first_name}
                </Button>
              )}
              <Button variant="outline" onClick={() => setViewProfile(null)} className="border-white/20 text-white/60">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Bookings View Component
const BookingsView = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [reason, setReason] = useState('');
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    try {
      const params = cancelReason ? `?reason=${encodeURIComponent(cancelReason)}` : '';
      await axios.put(`${API}/booking/${cancelModal.id}/cancel${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Booking cancelled — stand-in has been notified');
      setCancelModal(null);
      setCancelReason('');
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleModal || selectedDates.length === 0) { toast.error('Please select new dates'); return; }
    const dates = selectedDates.map(d => d.toISOString().split('T')[0]).sort();
    try {
      await axios.put(`${API}/booking/${rescheduleModal.id}/reschedule`, {
        new_dates: dates,
        reason: reason || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Booking rescheduled — stand-in has been notified');
      setRescheduleModal(null);
      setSelectedDates([]);
      setReason('');
      fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Bookings</h1>
      <p className="text-white/60 mb-8">Manage your confirmed stand-in bookings</p>

      {bookings.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No bookings yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div key={booking.id} className="bg-[#121212] border border-white/5 rounded-xl p-6" data-testid={`booking-${booking.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-white font-semibold">{booking.standin_name}</h3>
                  <div className="flex items-center gap-4 mt-2 text-white/60 text-sm">
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="w-3.5 h-3.5" />
                      {booking.dates?.length > 0 ? booking.dates.join(', ') : 'TBD'}
                    </span>
                    <span>£{booking.rate}/day</span>
                  </div>
                  {/* Contact details for booked stand-ins */}
                  {(booking.contact_email || booking.phone) && (
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                      {booking.contact_email && (
                        <a href={`mailto:${booking.contact_email}`} className="text-[#D4AF37] hover:underline">{booking.contact_email}</a>
                      )}
                      {booking.phone && (
                        <a href={`tel:${booking.phone}`} className="text-white/60">{booking.phone}</a>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                    booking.status === 'confirmed' ? 'bg-[#10B981]/20 text-[#10B981]' :
                    booking.status === 'pending' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                    booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                    booking.status === 'rescheduled' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-white/10 text-white/60'
                  }`}>
                    {booking.status}
                  </span>
                </div>
              </div>
              {/* Action buttons */}
              {booking.status !== 'cancelled' && (
                <div className="flex gap-2 mt-4 pt-3 border-t border-white/5">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setRescheduleModal(booking); setSelectedDates([]); }}
                    className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs"
                    data-testid={`reschedule-booking-${booking.id}`}
                  >
                    <CalendarIcon className="w-3.5 h-3.5 mr-1" /> Reschedule
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCancelModal(booking)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                    data-testid={`cancel-booking-${booking.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Cancel
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Cancel Modal */}
      {cancelModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setCancelModal(null)}>
          <div className="bg-[#121212] rounded-xl p-6 w-full max-w-md border border-white/10" onClick={e => e.stopPropagation()} data-testid="cancel-modal">
            <h3 className="text-white font-semibold text-lg mb-2">Cancel Booking</h3>
            <p className="text-white/60 text-sm mb-4">
              Cancel booking for <strong className="text-white">{cancelModal.standin_name}</strong>?
              They will be notified immediately.
            </p>
            <div className="mb-4">
              <Label className="text-white/80 text-sm mb-1 block">Reason (optional)</Label>
              <Textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="e.g. Production schedule change, filming moved..."
                rows={2}
                className="bg-[#0A0A0A] border-white/10 text-white resize-none"
                data-testid="cancel-reason-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setCancelModal(null)} className="border-white/20 text-white/60">
                Back
              </Button>
              <Button size="sm" onClick={handleCancel} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-btn">
                Cancel Booking
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setRescheduleModal(null)}>
          <div className="bg-[#121212] rounded-xl p-6 w-full max-w-md border border-white/10" onClick={e => e.stopPropagation()} data-testid="reschedule-modal">
            <h3 className="text-white font-semibold text-lg mb-2">Reschedule Booking</h3>
            <p className="text-white/60 text-sm mb-4">
              Reschedule <strong className="text-white">{rescheduleModal.standin_name}</strong>. They will be notified with the new dates.
            </p>
            <div className="mb-4">
              <Label className="text-white/80 text-sm mb-2 block">New Date(s) *</Label>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-2 flex justify-center">
                <Calendar
                  mode="multiple"
                  selected={selectedDates}
                  onSelect={setSelectedDates}
                  disabled={(date) => date < new Date()}
                  className="text-white"
                  data-testid="reschedule-calendar"
                />
              </div>
              {selectedDates.length > 0 && (
                <p className="text-[#D4AF37] text-xs mt-2">
                  Selected: {selectedDates.map(d => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })).join(', ')}
                </p>
              )}
            </div>
            <div className="mb-4">
              <Label className="text-white/80 text-sm mb-1 block">Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                placeholder="e.g. Filming location changed, schedule update..."
                rows={2}
                className="bg-[#0A0A0A] border-white/10 text-white resize-none"
                data-testid="reschedule-reason-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setRescheduleModal(null)} className="border-white/20 text-white/60">
                Back
              </Button>
              <Button size="sm" onClick={handleReschedule} className="gold-gradient text-black font-semibold" data-testid="confirm-reschedule-btn">
                Reschedule & Notify
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Invoices View Component
const InvoicesView = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailModal, setEmailModal] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/admin/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch {
      try {
        const response = await axios.get(`${API}/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices(response.data);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDateUK = (dateStr) => {
    if (!dateStr) return '';
    try {
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const yr = parts[2].length === 4 ? parts[2] : `20${parts[2]}`;
          return `${parts[0]}.${parts[1]}.${yr}`;
        }
      }
      const d = new Date(dateStr + (dateStr.length === 10 && !dateStr.includes('T') ? 'T00:00:00' : ''));
      if (isNaN(d.getTime())) return dateStr;
      return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
    } catch { return dateStr; }
  };

  const downloadPDF = (invoiceId) => {
    window.open(`${API}/admin/invoices/${invoiceId}/pdf?token=${token}`, '_blank');
  };

  const sendEmail = async () => {
    if (!emailTo.trim() || !emailModal) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/admin/invoices/${emailModal.id}/email?to_email=${encodeURIComponent(emailTo.trim())}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toast.success(`Invoice emailed to ${emailTo}`);
        setEmailModal(null);
        setEmailTo('');
      } else {
        toast.error(res.data.message || 'Send failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Invoices</h1>
      <p className="text-white/60 mb-8">View, download and share invoices with your accounts team</p>

      {invoices.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No invoices yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="bg-[#121212] border border-white/5 rounded-xl p-6" data-testid={`prod-invoice-${invoice.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-[#D4AF37] font-mono text-sm">{invoice.invoice_number}</span>
                    <span className={`px-2 py-0.5 text-xs rounded capitalize ${
                      invoice.status === 'paid' ? 'bg-[#10B981]/20 text-[#10B981]' :
                      invoice.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-white/10 text-white/60'
                    }`}>{invoice.status}</span>
                  </div>
                  <h3 className="text-white font-semibold">{invoice.client_name || invoice.production_name} — {invoice.project_name}</h3>
                  <p className="text-white/40 text-sm mt-1">
                    {invoice.line_items?.length || 0} stand-in{(invoice.line_items?.length || 0) !== 1 ? 's' : ''} | {formatDateUK(invoice.submitted_date || invoice.created_at)}
                  </p>
                </div>
                <p className="text-[#D4AF37] font-bold text-2xl">£{parseFloat(invoice.total || 0).toFixed(2)}</p>
              </div>

              {/* Stand-in names */}
              {invoice.line_items?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {invoice.line_items.map((item, idx) => (
                    <span key={idx} className="px-2 py-1 bg-white/5 text-white/70 text-xs rounded">
                      {item.standin_name} — £{parseFloat(item.amount || 0).toFixed(2)}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => downloadPDF(invoice.id)}
                  className="bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 text-xs"
                  data-testid={`download-pdf-${invoice.id}`}
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> Download PDF
                </Button>
                <Button
                  size="sm"
                  onClick={() => { setEmailModal(invoice); setEmailTo(''); }}
                  variant="outline"
                  className="border-white/10 text-white/60 hover:text-white text-xs"
                  data-testid={`email-invoice-${invoice.id}`}
                >
                  <Send className="w-3 h-3 mr-1" /> Email to Accounts
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setEmailModal(null)}>
          <div className="bg-[#121212] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold mb-1">Email Invoice</h3>
            <p className="text-white/40 text-sm mb-4">Send {emailModal.invoice_number} to your accounts team</p>
            <Input
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
              placeholder="accounts@production.com"
              type="email"
              className="bg-[#0A0A0A] border-white/10 text-white h-12 mb-4"
              data-testid="email-invoice-input"
            />
            <div className="flex gap-3">
              <Button
                onClick={sendEmail}
                disabled={sending || !emailTo.trim()}
                className="gold-gradient text-black font-semibold flex-1"
                data-testid="send-email-btn"
              >
                {sending ? 'Sending...' : 'Send Invoice'}
              </Button>
              <Button variant="outline" onClick={() => setEmailModal(null)} className="border-white/20 text-white/60">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Settings View Component
const SettingsView = () => {
  const { user } = useAuth();
  
  return (
    <div className="max-w-2xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Settings</h1>
      <p className="text-white/60 mb-8">Manage your account settings</p>

      <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
        <h2 className="font-semibold text-white mb-4">Account Information</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-white/60 text-sm">Email (Login)</Label>
            <p className="text-white">{user?.email}</p>
          </div>
          <div>
            <Label className="text-white/60 text-sm">Account Status</Label>
            <p className={`capitalize ${user?.status === 'approved' ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
              {user?.status}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductionDashboard;
